/**
 * 工单详情 + 履约动作。
 *
 * ══ 一、动作按钮按当前状态只给**合法流转** ══
 *
 * 状态机在服务端（`apps/api/src/modules/provider.routes.ts`），界面只是把它渲染出来：
 *
 *   pending 待受理 ──接单──▶ matched 已派单 ──开始履约──▶ serving 服务中 ──完成──▶ done 已完成
 *   scheduled 已预约 ──接单──────┘                    │
 *                              └──未到场──▶ no_show 未到场   └──取消（必填原因）──▶ cancelled
 *
 *   · `serving` 只能由 `matched` / `scheduled` 进入（**不能跳级**）；
 *   · `done` / `no_show` / `cancelled` 是终态，不再出现任何动作入口。
 *
 * **这层过滤不是权限，是「少给一个必然报错的按钮」。** 服务端仍然逐次裁决：
 * 即使用户绕过界面直接调接口，非法流转会拿到 409 的中文原文（含「允许…的状态」清单），
 * 界面把那段原文照实显示。
 *
 * ══ 二、健康数据：**只有成员单独授权才出现** ══
 *
 * 接口在未授权时**连 SQL 都不执行**，响应里根本没有 `authorizedData` 键。
 * 界面因此必须把「未授权」写清楚，而不是留空 —— 留白会被读成「数据加载失败」，
 * 服务方会反复刷新、甚至打电话来问，而真实原因是「平台按设计没给」。
 * 逐字文案见下方 `NoAuthorizationNotice`。
 *
 * ══ 三、本页**不出现**成员档案字段 ══
 *
 * 接口刻意不下发 `fullName` / `memberNo`；前端也**不许自己造**（不去别的接口取、不拼接）。
 * 页面上只有 `member.name` 与 `member.phone` —— 履约需要的全部身份信息。
 */
import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { providerApi, ApiError } from '../lib/api.ts';
import type { AuthorizedData, ProviderBookingDetail } from '../lib/api.ts';
import { useLoad } from '../lib/useLoad.ts';
import {
  bookingStatusText,
  bookingStatusTone,
  formatDateTime,
  formatNumber,
  formatText,
  slotRangeText,
} from '../lib/format.ts';
import { Badge, Card, Kv, PageHead } from '../components/ui.tsx';
import { ErrorBox, LoadingBox, errorText } from '../components/StateBox.tsx';

/**
 * 未授权时的逐字说明。**这几句话是平台隐私口径在界面上的落点**：
 * 它必须同时说清三件事 —— 没有健康数据、原因是没有授权（不是加载失败）、以及授权入口在哪。
 */
const NO_AUTHORIZATION_TITLE = '该成员未授权任何健康数据，因此本页不含健康信息';
const NO_AUTHORIZATION_DESC =
  '服务方默认只能看到履约必需信息（成员姓名、联系电话、服务项目、预约时间、成员备注、状态）。' +
  '健康、指标、报告与财富数据不在默认下发范围内：只有当成员在 C 端「隐私与授权」里单独授权给本机构后，' +
  '本页才会出现对应数据域。这不是数据加载失败，平台按设计没有下发这些字段。' +
  '如需查看，请联系成员本人在成员端逐项授权（可随时撤回）。';

/** 可用于取消 / 未到场 / 完成的就地表单。 */
type OpenForm = 'none' | 'complete' | 'cancel' | 'no_show';

function NoAuthorizationNotice() {
  return (
    <div className="no-auth" data-testid="provider-no-auth">
      <div className="na-t">{NO_AUTHORIZATION_TITLE}</div>
      <div>{NO_AUTHORIZATION_DESC}</div>
    </div>
  );
}

/** 已授权数据域：**必须显著标注来源**，并列出 scopes[]。 */
function AuthorizedDataBlock({ data }: { data: AuthorizedData }) {
  return (
    <div className="auth-box" data-testid="provider-authorized-data">
      <span className="ab-src">🔑 数据来源：{formatText(data.dataSource)}</span>
      <div className="ab-note">{formatText(data.dataSourceNote)}</div>

      <div style={{ marginTop: 12 }}>
        <div className="text-tiny muted">本次授权覆盖的数据域（scopes）：</div>
        {data.scopes.length === 0 ? (
          <div className="text-tiny muted" style={{ marginTop: 6 }}>
            接口未给出数据域清单。
          </div>
        ) : (
          <div className="grid3" style={{ marginTop: 8 }}>
            {data.scopes.map((scope) => (
              <div key={scope.scope} className="auth-scope">
                <div className="as-name">{formatText(scope.label)}</div>
                <div className="as-key">{formatText(scope.scope)}</div>
                <div className="text-tiny muted" style={{ marginTop: 6 }}>
                  字段：{scope.fields.length > 0 ? scope.fields.join('、') : '未提供'}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {(data.latestHealthScore !== undefined ||
        data.physioVitals !== undefined ||
        data.enterpriseScore !== undefined ||
        data.wealthScore !== undefined) && (
        <>
          <div className="divider" />
          <div className="text-tiny muted">授权范围内的具体数据（仅在成员授权后下发）：</div>
          <div className="stack" style={{ marginTop: 8 }}>
            {data.latestHealthScore !== undefined && (
              <div className="kv">
                <Kv label="最新健康评分" value={formatNumber(data.latestHealthScore.score)} />
                <Kv label="评级" value={formatText(data.latestHealthScore.gradeText)} text />
                <Kv label="测量时间" value={formatDateTime(data.latestHealthScore.measuredAt)} />
              </div>
            )}
            {data.enterpriseScore !== undefined && (
              <div className="kv">
                <Kv label="企业健康评分" value={formatNumber(data.enterpriseScore.score)} />
                <Kv label="评语" value={formatText(data.enterpriseScore.gradeComment)} text />
                <Kv label="测量时间" value={formatDateTime(data.enterpriseScore.measuredAt)} />
              </div>
            )}
            {data.wealthScore !== undefined && (
              <div className="kv">
                <Kv label="财富健康评分" value={formatNumber(data.wealthScore.score)} />
                <Kv label="评级" value={formatText(data.wealthScore.gradeText)} text />
                <Kv label="测量时间" value={formatDateTime(data.wealthScore.measuredAt)} />
              </div>
            )}
            {data.physioVitals !== undefined && (
              <div className="tbl-wrap">
                <table className="tbl">
                  <thead>
                    <tr>
                      <th>指标</th>
                      <th>数值</th>
                      <th>单位</th>
                      <th>参考区间</th>
                      <th>状态</th>
                      <th>采集时间</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.physioVitals.map((v) => (
                      <tr key={v.code}>
                        <td>
                          <b>{formatText(v.name)}</b>
                        </td>
                        <td className="mono-num">{formatText(v.valueText)}</td>
                        <td>{formatText(v.unit)}</td>
                        <td>{formatText(v.refRange)}</td>
                        <td>{formatText(v.status)}</td>
                        <td>{formatDateTime(v.capturedAt)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

export function BookingDetailPage() {
  const params = useParams();
  const id = params.id ?? '';
  const detail = useLoad<ProviderBookingDetail>((signal) => providerApi.booking(id, signal), [id]);

  const [form, setForm] = useState<OpenForm>('none');
  const [reason, setReason] = useState('');
  const [note, setNote] = useState('');
  const [summary, setSummary] = useState('');
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState('');
  const [actionOk, setActionOk] = useState('');

  const booking = detail.data;
  const status = booking?.status ?? '';

  const resetForms = (): void => {
    setForm('none');
    setReason('');
    setNote('');
    setSummary('');
  };

  /** 统一执行一次履约动作：**无论成功失败都照实显示服务端原文**，成功后整页刷新。 */
  const run = async (label: string, action: () => Promise<ProviderBookingDetail>): Promise<void> => {
    if (busy) return;
    setBusy(true);
    setActionError('');
    setActionOk('');
    try {
      const updated = await action();
      setActionOk(`已${label}，工单当前状态：${updated.statusLabel || bookingStatusText(updated.status)}`);
      resetForms();
      detail.reload();
    } catch (err) {
      // 409 = 状态流转不合法（含「允许…的状态」清单）、400 = 必填项缺失、403 = 主体准入失效
      setActionError(err instanceof ApiError ? err.message : err instanceof Error ? err.message : '操作失败，请稍后重试');
    } finally {
      setBusy(false);
    }
  };

  const canAccept = status === 'pending' || status === 'matched' || status === 'scheduled';
  const canStart = status === 'matched' || status === 'scheduled';
  const canComplete = status === 'serving';
  const canCancel = status === 'pending' || status === 'matched' || status === 'scheduled';
  const canNoShow = status === 'pending' || status === 'matched' || status === 'scheduled';
  const isTerminal = status === 'done' || status === 'no_show' || status === 'cancelled';
  const hasAnyAction = canAccept || canStart || canComplete || canCancel || canNoShow;

  return (
    <>
      <PageHead
        title="工单详情与履约"
        sub="BOOKING DETAIL · 每一次动作都会写入成员可见的履约时间轴"
        actions={
          <div className="row">
            <Link className="btn sm" to="/bookings">
              返回列表
            </Link>
            <button type="button" className="btn sm" onClick={detail.reload} disabled={detail.loading}>
              刷新
            </button>
          </div>
        }
      />

      {detail.loading && <LoadingBox text="正在读取工单…" />}
      {!detail.loading && detail.error !== null && (
        // 越权的工单返回 404（不是 403，避免探测 id 是否存在）；文案原样显示
        <ErrorBox message={errorText(detail.error)} onRetry={detail.reload} />
      )}

      {!detail.loading && detail.error === null && booking !== null && (
        <>
          <Card title="工单" sub="BOOKING" actions={<Badge tone={bookingStatusTone(booking.status)}>{booking.statusLabel || bookingStatusText(booking.status)}</Badge>}>
            <div className="kv">
              <Kv label="工单号" value={formatText(booking.id)} />
              <Kv label="状态" value={`${booking.statusLabel || bookingStatusText(booking.status)}（${formatText(booking.status)}）`} text />
              <Kv label="创建时间" value={formatDateTime(booking.createdAt)} />
              <Kv label="成员姓名" value={formatText(booking.member.name)} text />
              <Kv label="联系电话" value={formatText(booking.member.phone)} />
              <Kv label="服务项目" value={formatText(booking.service.resourceName)} text />
              <Kv label="号源" value={formatText(booking.service.slotLabel)} text />
              <Kv label="服务时间" value={slotRangeText(booking.service.startAt, booking.service.endAt)} />
              <Kv label="更新时间" value={formatDateTime(booking.updatedAt)} />
            </div>
            <div className="divider" />
            <div className="kv-l">成员备注</div>
            <div className="kv-v text">{formatText(booking.memberNote)}</div>
          </Card>

          <Card title="成员授权数据" sub="MEMBER AUTHORIZATION · 默认不下发">
            {booking.authorizedData !== undefined ? (
              <AuthorizedDataBlock data={booking.authorizedData} />
            ) : (
              <NoAuthorizationNotice />
            )}
            <div className="field-hint">
              本次读取覆盖的数据域（接口下发的 grantedScopes）：
              {booking.grantedScopes.length === 0 ? '（空 —— 未授权任何数据域）' : booking.grantedScopes.join('、')}
            </div>
          </Card>

          <Card title="履约动作" sub="TRANSITIONS">
            {actionOk !== '' && (
              <div className="alert-chip ok">
                <span>✓</span>
                <span>{actionOk}</span>
              </div>
            )}
            {actionError !== '' && (
              <div className="alert-chip danger">
                <span>!</span>
                <span>{actionError}</span>
              </div>
            )}

            {isTerminal && !hasAnyAction && (
              <div className="perm-block">
                <div className="pb-t">本单已处于终态：{booking.statusLabel || bookingStatusText(booking.status)}</div>
                终态工单（已完成 / 未到场 / 已取消）不再提供任何流转入口 ——
                平台不允许从终态再出发。如需处置，请联系平台运营。
              </div>
            )}

            {hasAnyAction && (
              <div className="action-panel">
                <div className="ap-t">可执行的动作（按当前状态「{booking.statusLabel || bookingStatusText(booking.status)}」给出）</div>
                <div className="ap-s">
                  这里只列出当前状态下合法的流转；服务端仍是唯一裁决者，非法流转会返回 409 的中文说明。
                </div>
                <div className="row wrap">
                  {canAccept && (
                    <button
                      type="button"
                      className="btn primary"
                      disabled={busy}
                      onClick={() => void run('接单', () => providerApi.accept(booking.id))}
                    >
                      接单
                    </button>
                  )}
                  {canStart && (
                    <button
                      type="button"
                      className="btn"
                      disabled={busy}
                      onClick={() => void run('开始履约', () => providerApi.start(booking.id))}
                    >
                      开始履约
                    </button>
                  )}
                  {canComplete && (
                    <button
                      type="button"
                      className="btn primary"
                      disabled={busy}
                      onClick={() => setForm(form === 'complete' ? 'none' : 'complete')}
                    >
                      完成履约
                    </button>
                  )}
                  {canNoShow && (
                    <button
                      type="button"
                      className="btn"
                      disabled={busy}
                      onClick={() => setForm(form === 'no_show' ? 'none' : 'no_show')}
                    >
                      成员未到场
                    </button>
                  )}
                  {canCancel && (
                    <button
                      type="button"
                      className="btn danger"
                      disabled={busy}
                      onClick={() => setForm(form === 'cancel' ? 'none' : 'cancel')}
                    >
                      取消工单
                    </button>
                  )}
                </div>

                {form === 'complete' && (
                  <>
                    <div className="divider" />
                    <div className="field">
                      <label className="field-label" htmlFor="act-summary">
                        履约结论（可留空）
                      </label>
                      <textarea
                        id="act-summary"
                        className="field-input"
                        rows={3}
                        maxLength={1000}
                        placeholder="例如：14 天疗程按期完成，指标复查已安排"
                        value={summary}
                        onChange={(e) => setSummary(e.target.value)}
                      />
                      <div className="field-hint">结论文本会写入成员可见的履约时间轴。</div>
                    </div>
                    <button
                      type="button"
                      className="btn primary"
                      disabled={busy}
                      onClick={() => void run('完成履约', () => providerApi.complete(booking.id, summary.trim()))}
                    >
                      {busy ? '正在提交…' : '确认完成履约'}
                    </button>
                  </>
                )}

                {form === 'cancel' && (
                  <>
                    <div className="divider" />
                    <div className="field">
                      <label className="field-label" htmlFor="act-reason">
                        取消原因（必填）
                      </label>
                      <textarea
                        id="act-reason"
                        className="field-input"
                        rows={3}
                        maxLength={500}
                        placeholder="例如：本机构当日设备检修，无法按期提供服务"
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                      />
                      <div className="field-hint">
                        原因会写入成员可见的履约时间轴，并同时占用该号源的名额回补逻辑（取消即退号）。
                        未填写原因时接口会返回 400，界面不允许提交空原因。
                      </div>
                    </div>
                    <button
                      type="button"
                      className="btn danger"
                      disabled={busy || reason.trim().length === 0}
                      onClick={() => void run('取消工单', () => providerApi.cancel(booking.id, reason.trim()))}
                    >
                      {busy ? '正在提交…' : '确认取消工单'}
                    </button>
                  </>
                )}

                {form === 'no_show' && (
                  <>
                    <div className="divider" />
                    <div className="field">
                      <label className="field-label" htmlFor="act-note">
                        未到场说明（必填）
                      </label>
                      <textarea
                        id="act-note"
                        className="field-input"
                        rows={3}
                        maxLength={500}
                        placeholder="例如：约定时间等候 60 分钟，成员未到且电话未接通"
                        value={note}
                        onChange={(e) => setNote(e.target.value)}
                      />
                      <div className="field-hint">
                        说明会写入成员可见的履约时间轴。标记未到场**不退号** ——
                        名额确实被占用过（机构为此空出了一个时段），退号会让排期口径失真。
                      </div>
                    </div>
                    <button
                      type="button"
                      className="btn"
                      disabled={busy || note.trim().length === 0}
                      onClick={() => void run('登记未到场', () => providerApi.noShow(booking.id, note.trim()))}
                    >
                      {busy ? '正在提交…' : '确认登记未到场'}
                    </button>
                  </>
                )}
              </div>
            )}
          </Card>

          <Card title="履约时间轴" sub={`TIMELINE · ${formatNumber(booking.timeline.length)} 条`}>
            {booking.timeline.length === 0 ? (
              <div className="state-box">
                <div className="st-title">接口未返回任何时间轴事件</div>
                <div>本单在平台侧还没有产生事件记录。</div>
              </div>
            ) : (
              <div className="timeline">
                {booking.timeline.map((event, index) => (
                  <div className="tl-item" key={`${event.at}-${String(index)}`}>
                    <div className="tl-at">{formatDateTime(event.at)}</div>
                    <div className="tl-text">{formatText(event.text)}</div>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </>
      )}
    </>
  );
}
